import { useState } from "react";
import { Copy, Check, RotateCcw, FileText, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";

const LIMITS = { title: 30, short: 80, full: 4000 };
const strip = (u) => (u || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

export function listingTemplate(name, f = {}) {
  const church = name || "Our Church";
  const place = [f.city, f.state].filter(Boolean).join(", ");
  const from = place ? ` from ${place}` : "";
  const links = [
    f.website && `Website: ${strip(f.website)}`,
    f.social_facebook && `Facebook: ${strip(f.social_facebook)}`,
    f.social_youtube && `YouTube: ${strip(f.social_youtube)}`,
    f.social_x && `X: ${strip(f.social_x)}`,
  ].filter(Boolean);
  const title = `${church} TV`.length <= LIMITS.title ? `${church} TV` : church.slice(0, LIMITS.title);
  const short = `Watch ${church} live and catch up on recent services.`;
  const full = [
    `Worship with ${church}${from} wherever you are. Watch our services live on your TV and catch up on recent messages any time.`,
    "",
    "What you can do:",
    "• Watch our services live as they happen",
    "• Catch up on recent sermons and special events",
    "• Share the experience with family and friends at home",
    "",
    `Whether you're part of the ${church} family or visiting for the first time, we'd love for you to join us.`,
    ...(links.length ? ["", "Stay connected:", ...links] : []),
  ].join("\n");
  return { title, short: short.length <= LIMITS.short ? short : `Watch ${church} live.`.slice(0, LIMITS.short), full };
}

function Field({ k, label, value, onChange, rows }) {
  const [copied, setCopied] = useState(false);
  const over = value.length > LIMITS[k];
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Couldn't copy. Select the text and copy it manually."); }
  };
  const cls = "w-full rounded-lg bg-gray-950 border px-3 py-2 text-sm text-white outline-none focus:border-amber-500/60 " + (over ? "border-red-500/60" : "border-gray-800");
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-gray-300 text-xs">{label}</Label>
        <div className="flex items-center gap-3">
          <span data-testid={`listing-${k}-count`} className={"text-[11px] font-mono " + (over ? "text-red-400" : "text-gray-500")}>{value.length}/{LIMITS[k]}</span>
          <button type="button" data-testid={`listing-${k}-copy`} onClick={copy} className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
            {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
          </button>
        </div>
      </div>
      {rows ? <textarea data-testid={`listing-${k}-input`} value={value} rows={rows} onChange={(e) => onChange(e.target.value)} className={cls + " resize-y leading-relaxed"} />
        : <input data-testid={`listing-${k}-input`} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

export function StoreListing({ listing, setListing, onReset, onSave, saving }) {
  const set = (k) => (v) => setListing({ ...listing, [k]: v });
  return (
    <div className="rounded-2xl border border-gray-800 bg-card p-5 space-y-4" data-testid="store-listing">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-display font-semibold text-white flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" /> Store listing text
          <span className="text-xs font-normal text-gray-500">ready to paste into Roku, Google Play and Amazon</span></p>
        <div className="flex items-center gap-3">
          <button type="button" data-testid="listing-reset" onClick={onReset} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"><RotateCcw className="h-3.5 w-3.5" /> Reset to template</button>
          <button type="button" data-testid="listing-save" onClick={onSave} disabled={saving} className="h-8 px-3 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save text
          </button>
        </div>
      </div>
      <Field k="title" label="Store title" value={listing.title} onChange={set("title")} />
      <Field k="short" label="Short description" value={listing.short} onChange={set("short")} />
      <Field k="full" label="Full description" value={listing.full} onChange={set("full")} rows={11} />
      <p className="text-[11px] text-gray-500">Limits follow the strictest store (Google Play: 30-character title, 80-character short description, 4,000-character full description).</p>
    </div>
  );
}
